import {
  DATE_OPERATORS,
  ENUM_OPERATORS,
  NUMBER_OPERATORS,
  QueryFieldDefinition,
  TEXT_OPERATORS,
} from './types'

/**
 * Queryable fields for the DEAL module (CRM pipeline). Registered here so the
 * query bar, the search page, and Benny's scrambl-view prompt all read the
 * same field list; the Prisma side lives in the API's DealQueryTranslator.
 */
export const DEAL_QUERY_FIELDS: QueryFieldDefinition[] = [
  {
    key: 'text',
    label: 'Text',
    type: 'text',
    description: 'Searches name and description',
    example: 'text ~ "renewal"',
    operators: ['~', '!~'],
  },
  {
    key: 'name',
    aliases: ['title'],
    label: 'Name',
    type: 'text',
    example: 'name ~ "website"',
    operators: TEXT_OPERATORS,
    sortable: true,
  },
  {
    key: 'description',
    label: 'Description',
    type: 'text',
    operators: ['~', '!~', 'IS EMPTY', 'IS NOT EMPTY'],
  },
  {
    key: 'amount',
    aliases: ['value', 'worth', 'dealValue'],
    label: 'Amount',
    type: 'number',
    description: 'Deal value in the workspace currency',
    example: 'amount > 10000',
    operators: NUMBER_OPERATORS,
    sortable: true,
  },
  {
    key: 'stage',
    label: 'Stage',
    type: 'enum',
    description: 'Matches the pipeline stage by name or id (e.g. "Proposal")',
    example: 'stage = "Proposal"',
    operators: ENUM_OPERATORS,
    optionSource: 'statuses',
    sortable: true,
  },
  {
    key: 'stageType',
    strictEnumValues: true,
    aliases: ['stageTypeId', 'stageCategory'],
    label: 'Stage type',
    type: 'enum',
    description:
      'The stage category: NEW, QUALIFYING, ENGAGED, NEGOTIATING, WON, LOST, ON_HOLD. Open deals are stageType NOT IN (WON, LOST)',
    example: 'stageType NOT IN (WON, LOST)',
    operators: ENUM_OPERATORS,
    enumValues: [
      { value: 'NEW', label: 'New' },
      { value: 'QUALIFYING', label: 'Qualifying' },
      { value: 'ENGAGED', label: 'Engaged' },
      { value: 'NEGOTIATING', label: 'Negotiating' },
      { value: 'WON', label: 'Won', aliases: ['CLOSED_WON', 'CLOSEDWON'] },
      { value: 'LOST', label: 'Lost', aliases: ['CLOSED_LOST', 'CLOSEDLOST'] },
      { value: 'ON_HOLD', label: 'On hold' },
    ],
  },
  {
    key: 'forecastCategory',
    strictEnumValues: true,
    aliases: ['forecast'],
    label: 'Forecast category',
    type: 'enum',
    example: 'forecastCategory IN (commit, best_case)',
    operators: ENUM_OPERATORS,
    enumValues: [
      { value: 'pipeline', label: 'Pipeline' },
      { value: 'best_case', label: 'Best case' },
      { value: 'commit', label: 'Commit' },
      { value: 'omitted', label: 'Omitted' },
    ],
  },
  {
    key: 'dealType',
    strictEnumValues: true,
    aliases: ['type'],
    label: 'Deal type',
    type: 'enum',
    example: 'dealType = RETAINER',
    operators: ENUM_OPERATORS,
    enumValues: [
      { value: 'SERVICES', label: 'Services' },
      { value: 'RETAINER', label: 'Retainer' },
      { value: 'LICENSING', label: 'Licensing' },
      { value: 'SUBSCRIPTION', label: 'Subscription' },
      { value: 'PRODUCT', label: 'Product' },
      { value: 'MIXED', label: 'Mixed' },
    ],
  },
  {
    key: 'probability',
    aliases: ['winProbability'],
    label: 'Probability',
    type: 'number',
    description: 'Rep confidence, 0-100',
    example: 'probability >= 50',
    operators: NUMBER_OPERATORS,
    sortable: true,
  },
  {
    key: 'closeDate',
    aliases: ['close', 'expectedClose', 'expectedCloseDate'],
    label: 'Close date',
    type: 'date',
    example: 'closeDate <= endOfMonth()',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'expectedStartDate',
    aliases: ['start', 'startDate'],
    label: 'Expected start',
    type: 'date',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'entity',
    aliases: ['client', 'company', 'account'],
    label: 'Entity',
    type: 'reference',
    description: "Matches the deal's entity by name",
    example: 'entity = "Acme Corp"',
    operators: ENUM_OPERATORS,
    optionSource: 'entities',
  },
  {
    key: 'contact',
    aliases: ['primaryContact'],
    label: 'Primary contact',
    type: 'reference',
    description: 'Matches the primary contact by name or email',
    example: 'contact ~ "Radia"',
    operators: ['=', '!=', '~', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
  },
  {
    key: 'assignee',
    aliases: ['owner', 'assignedTo', 'rep'],
    label: 'Assignee',
    type: 'user',
    description: 'The member the deal is assigned to',
    example: 'assignee = currentUser()',
    operators: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
    optionSource: 'members',
    supportsCurrentUser: true,
  },
  {
    key: 'creator',
    aliases: ['createdBy'],
    label: 'Creator',
    type: 'user',
    example: 'creator = currentUser()',
    operators: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
    optionSource: 'members',
    supportsCurrentUser: true,
  },
  {
    key: 'created',
    aliases: ['createdAt'],
    label: 'Created date',
    type: 'date',
    example: 'created >= -90d',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'updated',
    aliases: ['updatedAt'],
    label: 'Updated date',
    type: 'date',
    operators: DATE_OPERATORS,
    sortable: true,
  },
]
